import * as stylex from '@stylexjs/stylex'
import { type CSSProperties } from '../../types/StyleXCSSTypes'
import { calcUnit } from './utils'

export type CssValue = string | number
export type Position = NonNullable<CSSProperties['position']>
export type Display = NonNullable<CSSProperties['display']>
export type JustifyContent = NonNullable<CSSProperties['justifyContent']>
export type FlexDirection = NonNullable<CSSProperties['flexDirection']>
export type FlexWrap = NonNullable<CSSProperties['flexWrap']>
export type AlignItems = NonNullable<CSSProperties['alignItems']>
export type TextAlign = NonNullable<CSSProperties['textAlign']>
export type TextOverflow = NonNullable<CSSProperties['textOverflow']>
export type WhiteSpace = NonNullable<CSSProperties['whiteSpace']>
export type BorderStyle = NonNullable<CSSProperties['borderStyle']>
export type Overflow = NonNullable<CSSProperties['overflow']>
export type BoxSizing = NonNullable<CSSProperties['boxSizing']>
export type PointerEvents = NonNullable<CSSProperties['pointerEvents']>
export type BackgroundSize = NonNullable<CSSProperties['backgroundSize']>
export type BackgroundPosition = NonNullable<CSSProperties['backgroundPosition']>
export type BackgroundRepeat = NonNullable<CSSProperties['backgroundRepeat']>
export type BackgroundAttachment = NonNullable<CSSProperties['backgroundAttachment']>
export type BackgroundClip = NonNullable<CSSProperties['backgroundClip']>
export type BackgroundOrigin = NonNullable<CSSProperties['backgroundOrigin']>
export type TransformOrigin = NonNullable<CSSProperties['transformOrigin']>
export type Transform = NonNullable<CSSProperties['transform']>
export type TransformStyle = NonNullable<CSSProperties['transformStyle']>
export type UserSelect = NonNullable<CSSProperties['userSelect']>
export type CSSCursor = NonNullable<CSSProperties['cursor']>
export type BoxShadow = NonNullable<CSSProperties['boxShadow']>
export type BackgroundImage = NonNullable<CSSProperties['backgroundImage']>
export type BackgroundColor = NonNullable<CSSProperties['backgroundColor']>
export type BorderColor = NonNullable<CSSProperties['borderColor']>
export type BorderBottomColor = NonNullable<CSSProperties['borderBottomColor']>
export type BorderLeftColor = NonNullable<CSSProperties['borderLeftColor']>
export type BorderRightColor = NonNullable<CSSProperties['borderRightColor']>
export type BorderTopColor = NonNullable<CSSProperties['borderTopColor']>
export type Color = NonNullable<CSSProperties['color']>
export type ObjectFit = NonNullable<CSSProperties['objectFit']>
export type FontStyle = NonNullable<CSSProperties['fontStyle']>
export type FontFamily = NonNullable<CSSProperties['fontFamily']>
export type Visibility = NonNullable<CSSProperties['visibility']>

export const cursorStyles = stylex.create({
  cursor: (cursor: CSSCursor) => ({ cursor }),
})

export const boxShadowStyles = stylex.create({
  boxShadow: (boxShadow: BoxShadow) => ({ boxShadow }),
})

export const transformStyles = stylex.create({
  transform: (transform: Transform) => ({ transform }),
  transformOrigin: (transformOrigin: TransformOrigin) => ({ transformOrigin }),
  transformStyle: (transformStyle: TransformStyle) => ({ transformStyle }),
})

export const userSelectStyles = stylex.create({
  userSelect: (userSelect: UserSelect) => ({ userSelect }),
})

export const positionStyles = stylex.create<Record<Position, {
  position: Position
}>>({
  static: { position: 'static' },
  relative: { position: 'relative' },
  absolute: { position: 'absolute' },
  sticky: { position: 'sticky' },
  fixed: { position: 'fixed' },
  initial: { position: 'initial' },
  inherit: { position: 'inherit' },
  unset: { position: 'unset' },
})

export const lineHeightStyles = stylex.create({
  lineHeight: (lineHeight: CssValue) => ({ lineHeight }),
})

export const boxSizingStyles = stylex.create<Record<BoxSizing, {
  boxSizing: BoxSizing
}>>({
  'content-box': { boxSizing: 'content-box' },
  'border-box': { boxSizing: 'border-box' },
  'initial': { boxSizing: 'initial' },
  'inherit': { boxSizing: 'inherit' },
  'unset': { boxSizing: 'unset' },
})

export const displayStyles = stylex.create<Record<Display, {
  display: Display
}>>({
  'none': { display: 'none' },
  'inline': { display: 'inline' },
  'block': { display: 'block' },
  'list-item': { display: 'list-item' },
  'inline-list-item': { display: 'inline-list-item' },
  'inline-block': { display: 'inline-block' },
  'inline-table': { display: 'inline-table' },
  'table': { display: 'table' },
  'table-cell': { display: 'table-cell' },
  'table-column': { display: 'table-column' },
  'table-column-group': { display: 'table-column-group' },
  'table-footer-group': { display: 'table-footer-group' },
  'table-header-group': { display: 'table-header-group' },
  'table-row': { display: 'table-row' },
  'table-row-group': { display: 'table-row-group' },
  'flex': { display: 'flex' },
  'inline-flex': { display: 'inline-flex' },
  'grid': { display: 'grid' },
  'inline-grid': { display: 'inline-grid' },
  '-webkit-box': { display: '-webkit-box' },
  'run-in': { display: 'run-in' },
  'ruby': { display: 'ruby' },
  'ruby-base': { display: 'ruby-base' },
  'ruby-text': { display: 'ruby-text' },
  'ruby-base-container': { display: 'ruby-base-container' },
  'ruby-text-container': { display: 'ruby-text-container' },
  'contents': { display: 'contents' },
  'initial': { display: 'initial' },
  'inherit': { display: 'inherit' },
  'unset': { display: 'unset' },
  'flow-root': { display: 'flow-root' },
})

export const justifyContentStyles = stylex.create<Record<JustifyContent, {
  justifyContent: JustifyContent
}>>({
  'flex-start': { justifyContent: 'flex-start' },
  'flex-end': { justifyContent: 'flex-end' },
  'center': { justifyContent: 'center' },
  'stretch': { justifyContent: 'stretch' },
  'space-between': { justifyContent: 'space-between' },
  'space-around': { justifyContent: 'space-around' },
  'space-evenly': { justifyContent: 'space-evenly' },
  'initial': { justifyContent: 'initial' },
  'inherit': { justifyContent: 'inherit' },
  'unset': { justifyContent: 'unset' },
  'end': { justifyContent: 'end' },
  'left': { justifyContent: 'left' },
  'right': { justifyContent: 'right' },
  'start': { justifyContent: 'start' },
  'normal': { justifyContent: 'normal' },
  'safe center': { justifyContent: 'safe center' },
  'unsafe center': { justifyContent: 'unsafe center' },
})

export const alignItemsStyles = stylex.create<Record<AlignItems, {
  alignItems: AlignItems
}>>({
  'start': { alignItems: 'start' },
  'end': { alignItems: 'end' },
  'flex-start': { alignItems: 'flex-start' },
  'flex-end': { alignItems: 'flex-end' },
  'center': { alignItems: 'center' },
  'baseline': { alignItems: 'baseline' },
  'stretch': { alignItems: 'stretch' },
  'initial': { alignItems: 'initial' },
  'inherit': { alignItems: 'inherit' },
  'unset': { alignItems: 'unset' },
  'self-start': { alignItems: 'self-start' },
  'self-end': { alignItems: 'self-end' },
  'normal': { alignItems: 'normal' },
  'safe center': { alignItems: 'safe center' },
  'unsafe center': { alignItems: 'unsafe center' },
  'first baseline': { alignItems: 'first baseline' },
  'last baseline': { alignItems: 'last baseline' },
})

export const textAlignItemsStyles = stylex.create<Record<TextAlign, {
  textAlign: TextAlign
}>>({
  'initial': { textAlign: 'initial' },
  'inherit': { textAlign: 'inherit' },
  'unset': { textAlign: 'unset' },
  'left': { textAlign: 'left' },
  'right': { textAlign: 'right' },
  'center': { textAlign: 'center' },
  'justify': { textAlign: 'justify' },
  'end': { textAlign: 'end' },
  'start': { textAlign: 'start' },
  'match-parent': { textAlign: 'match-parent' },
})

export const textOverflowStyles = stylex.create<Record<TextOverflow, {
  textOverflow: TextOverflow
}>>({
  initial: { textOverflow: 'initial' },
  inherit: { textOverflow: 'inherit' },
  unset: { textOverflow: 'unset' },
  revert: { textOverflow: 'revert' },
  clip: { textOverflow: 'clip' },
  ellipsis: { textOverflow: 'ellipsis' },
})

export const whiteSpaceStyles = stylex.create<Record<WhiteSpace, {
  whiteSpace: WhiteSpace
}>>({
  'nowrap': { whiteSpace: 'nowrap' },
  'pre-line': { whiteSpace: 'pre-line' },
  'pre-wrap': { whiteSpace: 'pre-wrap' },
  'pre': { whiteSpace: 'pre' },
  'normal': { whiteSpace: 'normal' },
  'initial': { whiteSpace: 'initial' },
  'inherit': { whiteSpace: 'inherit' },
  'unset': { whiteSpace: 'unset' },
})

export const flexDirectionStyles = stylex.create<Record<FlexDirection, {
  flexDirection: FlexDirection
}>>({
  'row': { flexDirection: 'row' },
  'row-reverse': { flexDirection: 'row-reverse' },
  'column': { flexDirection: 'column' },
  'column-reverse': { flexDirection: 'column-reverse' },
  'initial': { flexDirection: 'initial' },
  'inherit': { flexDirection: 'inherit' },
  'unset': { flexDirection: 'unset' },
})

export const flexWrapStyles = stylex.create<Record<FlexWrap, {
  flexWrap: FlexWrap
}>>({
  'nowrap': { flexWrap: 'nowrap' },
  'wrap': { flexWrap: 'wrap' },
  'wrap-reverse': { flexWrap: 'wrap-reverse' },
  'initial': { flexWrap: 'initial' },
  'inherit': { flexWrap: 'inherit' },
  'unset': { flexWrap: 'unset' },
})

export const borderStyles = stylex.create<Record<BorderStyle, {
  borderStyle: BorderStyle
}>>({
  none: { borderStyle: 'none' },
  hidden: { borderStyle: 'hidden' },
  dotted: { borderStyle: 'dotted' },
  dashed: { borderStyle: 'dashed' },
  solid: { borderStyle: 'solid' },
  double: { borderStyle: 'double' },
  groove: { borderStyle: 'groove' },
  ridge: { borderStyle: 'ridge' },
  inset: { borderStyle: 'inset' },
  outset: { borderStyle: 'outset' },
  initial: { borderStyle: 'initial' },
  inherit: { borderStyle: 'inherit' },
  unset: { borderStyle: 'unset' },
})

export const borderTopStyles = stylex.create<Record<BorderStyle, {
  borderTopStyle: BorderStyle
}>>({
  none: { borderTopStyle: 'none' },
  hidden: { borderTopStyle: 'hidden' },
  dotted: { borderTopStyle: 'dotted' },
  dashed: { borderTopStyle: 'dashed' },
  solid: { borderTopStyle: 'solid' },
  double: { borderTopStyle: 'double' },
  groove: { borderTopStyle: 'groove' },
  ridge: { borderTopStyle: 'ridge' },
  inset: { borderTopStyle: 'inset' },
  outset: { borderTopStyle: 'outset' },
  initial: { borderTopStyle: 'initial' },
  inherit: { borderTopStyle: 'inherit' },
  unset: { borderTopStyle: 'unset' },
})

export const borderBottomStyles = stylex.create<Record<BorderStyle, {
  borderBottomStyle: BorderStyle
}>>({
  none: { borderBottomStyle: 'none' },
  hidden: { borderBottomStyle: 'hidden' },
  dotted: { borderBottomStyle: 'dotted' },
  dashed: { borderBottomStyle: 'dashed' },
  solid: { borderBottomStyle: 'solid' },
  double: { borderBottomStyle: 'double' },
  groove: { borderBottomStyle: 'groove' },
  ridge: { borderBottomStyle: 'ridge' },
  inset: { borderBottomStyle: 'inset' },
  outset: { borderBottomStyle: 'outset' },
  initial: { borderBottomStyle: 'initial' },
  inherit: { borderBottomStyle: 'inherit' },
  unset: { borderBottomStyle: 'unset' },
})

export const borderRightStyles = stylex.create<Record<BorderStyle, {
  borderRightStyle: BorderStyle
}>>({
  none: { borderRightStyle: 'none' },
  hidden: { borderRightStyle: 'hidden' },
  dotted: { borderRightStyle: 'dotted' },
  dashed: { borderRightStyle: 'dashed' },
  solid: { borderRightStyle: 'solid' },
  double: { borderRightStyle: 'double' },
  groove: { borderRightStyle: 'groove' },
  ridge: { borderRightStyle: 'ridge' },
  inset: { borderRightStyle: 'inset' },
  outset: { borderRightStyle: 'outset' },
  initial: { borderRightStyle: 'initial' },
  inherit: { borderRightStyle: 'inherit' },
  unset: { borderRightStyle: 'unset' },
})
export const borderLeftStyles = stylex.create<Record<BorderStyle, {
  borderLeftStyle: BorderStyle
}>>({
  none: { borderLeftStyle: 'none' },
  hidden: { borderLeftStyle: 'hidden' },
  dotted: { borderLeftStyle: 'dotted' },
  dashed: { borderLeftStyle: 'dashed' },
  solid: { borderLeftStyle: 'solid' },
  double: { borderLeftStyle: 'double' },
  groove: { borderLeftStyle: 'groove' },
  ridge: { borderLeftStyle: 'ridge' },
  inset: { borderLeftStyle: 'inset' },
  outset: { borderLeftStyle: 'outset' },
  initial: { borderLeftStyle: 'initial' },
  inherit: { borderLeftStyle: 'inherit' },
  unset: { borderLeftStyle: 'unset' },
})

export const overflowStyles = stylex.create<Record<Overflow, {
  overflow: Overflow
}>>({
  auto: { overflow: 'auto' },
  hidden: { overflow: 'hidden' },
  visible: { overflow: 'visible' },
  scroll: { overflow: 'scroll' },
  clip: { overflow: 'clip' },
  initial: { overflow: 'initial' },
  inherit: { overflow: 'inherit' },
  unset: { overflow: 'unset' },
})

export const pointerEventsStyles = stylex.create<Record<PointerEvents, {
  pointerEvents: PointerEvents
}>>({
  auto: { pointerEvents: 'auto' },
  none: { pointerEvents: 'none' },
  inherit: { pointerEvents: 'inherit' },
  unset: { pointerEvents: 'unset' },
  visiblePainted: { pointerEvents: 'visiblePainted' },
  visibleFill: { pointerEvents: 'visibleFill' },
  visibleStroke: { pointerEvents: 'visibleStroke' },
  visible: { pointerEvents: 'visible' },
  painted: { pointerEvents: 'painted' },
  fill: { pointerEvents: 'fill' },
  stroke: { pointerEvents: 'stroke' },
  all: { pointerEvents: 'all' },
  initial: { pointerEvents: 'initial' },
})

export const visibilityStyles = stylex.create<Record<Visibility, {
  visibility: Visibility
}>>({
  visible: { visibility: 'visible' },
  hidden: { visibility: 'hidden' },
  collapse: { visibility: 'collapse' },
  initial: { visibility: 'initial' },
  inherit: { visibility: 'inherit' },
  unset: { visibility: 'unset' },
})

export const backgroundStyles = stylex.create({
  backgroundImage: (backgroundImage: BackgroundImage) => ({ backgroundImage }),
  backgroundSize: (backgroundSize: BackgroundSize) => ({ backgroundSize }),
  backgroundPosition: (backgroundPosition: BackgroundPosition) => ({ backgroundPosition }),
  backgroundRepeat: (backgroundRepeat: BackgroundRepeat) => ({ backgroundRepeat }),
  backgroundAttachment: (backgroundAttachment: BackgroundAttachment) => ({ backgroundAttachment }),
  backgroundClip: (backgroundClip: BackgroundClip) => ({ backgroundClip }),
  backgroundOrigin: (backgroundOrigin: BackgroundOrigin) => ({ backgroundOrigin }),
})

export const spaceStyles = stylex.create({
  m: (value: CssValue) => ({ margin: calcUnit(value) }),
  mt: (value: CssValue) => ({ marginTop: calcUnit(value) }),
  mb: (value: CssValue) => ({ marginBottom: calcUnit(value) }),
  ml: (value: CssValue) => ({ marginLeft: calcUnit(value) }),
  mr: (value: CssValue) => ({ marginRight: calcUnit(value) }),
  mx: (value: CssValue) => ({
    marginLeft: calcUnit(value),
    marginRight: calcUnit(value),
  }),
  my: (value: CssValue) => ({
    marginBottom: calcUnit(value),
    marginTop: calcUnit(value),
  }),
  margin: (value: CssValue) => ({ margin: calcUnit(value) }),
  marginTop: (value: CssValue) => ({ marginTop: calcUnit(value) }),
  marginBottom: (value: CssValue) => ({ marginBottom: calcUnit(value) }),
  marginLeft: (value: CssValue) => ({ marginLeft: calcUnit(value) }),
  marginRight: (value: CssValue) => ({ marginRight: calcUnit(value) }),
  p: (value: CssValue) => ({ padding: calcUnit(value) }),
  pt: (value: CssValue) => ({ paddingTop: calcUnit(value) }),
  pb: (value: CssValue) => ({ paddingBottom: calcUnit(value) }),
  pl: (value: CssValue) => ({ paddingLeft: calcUnit(value) }),
  pr: (value: CssValue) => ({ paddingRight: calcUnit(value) }),
  px: (value: CssValue) => ({
    paddingLeft: calcUnit(value),
    paddingRight: calcUnit(value),
  }),
  py: (value: CssValue) => ({
    paddingBottom: calcUnit(value),
    paddingTop: calcUnit(value),
  }),
  padding: (value: CssValue) => ({ padding: calcUnit(value) }),
  paddingTop: (value: CssValue) => ({ paddingTop: calcUnit(value) }),
  paddingBottom: (value: CssValue) => ({ paddingBottom: calcUnit(value) }),
  paddingLeft: (value: CssValue) => ({ paddingLeft: calcUnit(value) }),
  paddingRight: (value: CssValue) => ({ paddingRight: calcUnit(value) }),
  gap: (value: CssValue) => ({ gap: calcUnit(value) }),
  rowGap: (value: CssValue) => ({ rowGap: calcUnit(value) }),
  columnGap: (value: CssValue) => ({ columnGap: calcUnit(value) }),
  scrollMarginTop: (value: CssValue) => ({ scrollMarginTop: calcUnit(value) }),
})
export type SpaceKey = keyof typeof spaceStyles

export const valueStyles = stylex.create({
  borderBottomLeftRadius: (value: CssValue) => ({ borderBottomLeftRadius: value }),
  borderBottomRightRadius: (value: CssValue) => ({ borderBottomRightRadius: value }),
  borderBottomWidth: (value: CssValue) => ({ borderBottomWidth: value }),
  borderLeftWidth: (value: CssValue) => ({ borderLeftWidth: value }),
  borderRadius: (value: CssValue) => ({ borderRadius: value }),
  borderRightWidth: (value: CssValue) => ({ borderRightWidth: value }),
  borderTopLeftRadius: (value: CssValue) => ({ borderTopLeftRadius: value }),
  borderTopRightRadius: (value: CssValue) => ({ borderTopRightRadius: value }),
  borderTopWidth: (value: CssValue) => ({ borderTopWidth: value }),
  borderWidth: (value: CssValue) => ({ borderWidth: value }),
  bottom: (value: CssValue) => ({ bottom: value }),
  flex: (value: CssValue) => ({ flex: value }),
  flexGrow: (value: CssValue) => ({ flexGrow: value }),
  flexShrink: (value: CssValue) => ({ flexShrink: value }),
  fontSize: (value: CssValue) => ({ fontSize: value }),
  fontWeight: (value: CssValue) => ({ fontWeight: value }),
  height: (value: CssValue) => ({ height: value }),
  left: (value: CssValue) => ({ left: value }),
  maxHeight: (value: CssValue) => ({ maxHeight: value }),
  maxWidth: (value: CssValue) => ({ maxWidth: value }),
  minHeight: (value: CssValue) => ({ minHeight: value }),
  minWidth: (value: CssValue) => ({ minWidth: value }),
  opacity: (value: CssValue) => ({ opacity: value }),
  right: (value: CssValue) => ({ right: value }),
  top: (value: CssValue) => ({ top: value }),
  width: (value: CssValue) => ({ width: value }),
  zIndex: (value: CssValue) => ({ zIndex: value }),
})
export type ValueKey = keyof typeof valueStyles

export const colorStyles = stylex.create({
  backgroundColor: (color: BackgroundColor) => ({ backgroundColor: color }),
  borderBottomColor: (color: BorderBottomColor) => ({ borderBottomColor: color }),
  borderColor: (color: BorderColor) => ({ borderColor: color }),
  borderLeftColor: (color: BorderLeftColor) => ({ borderLeftColor: color }),
  borderRightColor: (color: BorderRightColor) => ({ borderRightColor: color }),
  borderTopColor: (color: BorderTopColor) => ({ borderTopColor: color }),
  color: (color: Color) => ({ color }),
})
export type ColorKey = keyof typeof colorStyles

export const objectFitStyles = stylex.create({
  objectFit: (objectFit: ObjectFit) => ({ objectFit }),
})
export const fontStyles = stylex.create({
  fontFamily: (fontFamily: FontFamily) => ({ fontFamily }),
  fontStyle: (fontStyle: FontStyle) => ({ fontStyle }),
})

export const gridStyles = stylex.create({
  gridArea: (gridArea: string) => ({ gridArea }),
  gridAutoColumns: (gridAutoColumns: string) => ({ gridAutoColumns }),
  gridAutoFlow: (gridAutoFlow: string) => ({ gridAutoFlow }),
  gridAutoRows: (gridAutoRows: string) => ({ gridAutoRows }),
  gridColumn: (gridColumn: string) => ({ gridColumn }),
  gridColumnEnd: (gridColumnEnd: string) => ({ gridColumnEnd }),
  gridColumnGap: (gridColumnGap: string) => ({ gridColumnGap }),
  gridColumnStart: (gridColumnStart: string) => ({ gridColumnStart }),
  gridGap: (gridGap: string) => ({ gridGap }),
  gridRow: (gridRow: string) => ({ gridRow }),
  gridRowEnd: (gridRowEnd: string) => ({ gridRowEnd }),
  gridRowGap: (gridRowGap: string) => ({ gridRowGap }),
  gridRowStart: (gridRowStart: string) => ({ gridRowStart }),
  gridTemplate: (gridTemplate: string) => ({ gridTemplate }),
  gridTemplateAreas: (gridTemplateAreas: string) => ({ gridTemplateAreas }),
  gridTemplateColumns: (gridTemplateColumns: string) => ({ gridTemplateColumns }),
  gridTemplateRows: (gridTemplateRows: string) => ({ gridTemplateRows }),
})
